import type { Component } from '../router';
import { ApiError } from '../services/api';
import { requestAccountRecovery } from '../services/auth';

type SubmitState = 'idle' | 'sending' | 'sent' | 'error';

const SVG_NS = 'http://www.w3.org/2000/svg';

// The three steps shown on the page — sets expectations for an admin-mediated
// flow so a user isn't surprised that recovery isn't instant.
const STEPS: { title: string; detail: string }[] = [
  { title: 'Request recovery', detail: 'Enter your email and we’ll alert an administrator.' },
  { title: 'Verify your identity', detail: 'An admin contacts you on a registered channel to confirm it’s really you.' },
  { title: 'Sign in & set up again', detail: 'Your passkey is reset, so you can enroll a new one next time you sign in.' },
];

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

// A check-in-circle drawn as SVG elements (not innerHTML), matching the
// element-building convention used in LoginPage/icons.ts.
function checkBadge(): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '28');
  svg.setAttribute('height', '28');
  svg.setAttribute('aria-hidden', 'true');
  const circle = document.createElementNS(SVG_NS, 'circle');
  circle.setAttribute('cx', '12');
  circle.setAttribute('cy', '12');
  circle.setAttribute('r', '11');
  circle.setAttribute('fill', 'none');
  circle.setAttribute('stroke', 'currentColor');
  circle.setAttribute('stroke-width', '1.5');
  const check = document.createElementNS(SVG_NS, 'path');
  check.setAttribute('d', 'M7 12.5l3.2 3.2L17 9');
  check.setAttribute('fill', 'none');
  check.setAttribute('stroke', 'currentColor');
  check.setAttribute('stroke-width', '2');
  check.setAttribute('stroke-linecap', 'round');
  check.setAttribute('stroke-linejoin', 'round');
  svg.append(circle, check);
  return svg;
}

// errorCopy mirrors LoginPage: the server returns 200 for any email per the
// enumeration policy, but a 429 (rate limit) and 5xx still arrive as ApiError;
// anything else is a transport failure.
export function errorCopy(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return 'Too many requests — give it a minute, then try again.';
    return 'Something went wrong on our end. Please try again.';
  }
  return 'Could not reach the server. Check your connection.';
}

export class RecoveryPage implements Component {
  private root: HTMLElement | null = null;

  mount(host: HTMLElement): void {
    const section = el('section', 'recovery');
    const card = el('div', 'recovery__card');

    const brand = el('div', 'recovery__brand');
    const logo = el('img');
    logo.src = '/brand/logo.png';
    logo.alt = 'Bluefield Chemicals';
    logo.width = 360;
    logo.height = 166;
    brand.appendChild(logo);

    // Two-column body on desktop (intro beside form), stacked on phone.
    const body = el('div', 'recovery__body');
    body.append(this.buildIntro(), this.buildPanel());

    const back = el('p', 'recovery__back');
    const backLink = el('a', undefined, 'Back to sign in');
    backLink.setAttribute('href', '/login');
    back.appendChild(backLink);

    card.append(brand, body, back);
    section.appendChild(card);
    host.appendChild(section);
    this.root = section;
  }

  unmount(): void {
    this.root?.remove();
    this.root = null;
  }

  private buildIntro(): HTMLElement {
    const intro = el('div', 'recovery__intro');
    const title = el('h1', 'recovery__title', 'Lost your passkey?');
    const sub = el(
      'p',
      'recovery__sub',
      'We’ll help you get back in. For your security, an administrator verifies your identity before your passkey is reset.',
    );

    const steps = el('ol', 'recovery__steps');
    STEPS.forEach((step, i) => {
      const item = el('li', 'recovery__step');
      const num = el('span', 'recovery__step-num', String(i + 1));
      num.setAttribute('aria-hidden', 'true');
      const textWrap = el('div', 'recovery__step-text');
      textWrap.append(
        el('span', 'recovery__step-title', step.title),
        el('span', 'recovery__step-detail', step.detail),
      );
      item.append(num, textWrap);
      steps.appendChild(item);
    });

    intro.append(title, sub, steps);
    return intro;
  }

  private buildPanel(): HTMLElement {
    const panel = el('div', 'recovery__panel');
    const form = el('form', 'recovery__form');
    form.noValidate = true;

    const label = el('label', 'recovery__label', 'Email');
    label.htmlFor = 'recovery-email';

    const input = el('input', 'recovery__input');
    input.id = 'recovery-email';
    input.name = 'email';
    input.type = 'email';
    input.required = true;
    input.placeholder = 'name@example.com';
    input.autocomplete = 'email';
    input.spellcheck = false;
    input.autocapitalize = 'none';
    input.enterKeyHint = 'send';
    input.setAttribute('autocorrect', 'off');

    const submit = el('button', 'recovery__submit');
    submit.type = 'submit';
    submit.dataset.state = 'idle';
    const submitLabel = el('span', 'recovery__submit-label', 'Request recovery');
    submit.appendChild(submitLabel);

    const status = el('p', 'recovery__status');
    status.dataset.visible = 'false';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    const setState = (next: SubmitState, message?: string, tone?: 'success' | 'error'): void => {
      submit.dataset.state = next;
      submit.disabled = next === 'sending';
      if (message) {
        status.textContent = message;
        status.dataset.visible = 'true';
        if (tone) status.dataset.tone = tone;
        else delete status.dataset.tone;
      } else {
        status.dataset.visible = 'false';
      }
    };

    // Editing the email after an error returns the button to idle so the user
    // can retry a corrected address.
    input.addEventListener('input', () => {
      if (submit.dataset.state === 'error') setState('idle');
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (submit.disabled) return;
      setState('sending', 'Sending…');
      try {
        await requestAccountRecovery(input.value);
        // Per the enumeration policy the server replies 200 for any email; show
        // the same generic confirmation regardless of whether it matched.
        this.showConfirmation(panel);
      } catch (err) {
        setState('error', errorCopy(err), 'error');
      }
    });

    form.append(label, input, submit, status);
    panel.appendChild(form);

    queueMicrotask(() => input.focus());
    return panel;
  }

  // showConfirmation swaps the form for a success view in place — the demo
  // happy path. The copy is deliberately generic (no account-existence signal).
  private showConfirmation(panel: HTMLElement): void {
    panel.replaceChildren();
    const box = el('div', 'recovery__success');
    box.setAttribute('role', 'status');
    box.setAttribute('aria-live', 'polite');

    const badge = el('div', 'recovery__success-badge');
    badge.appendChild(checkBadge());

    const heading = el('h2', 'recovery__success-title', 'Request received');
    const lead = el(
      'p',
      'recovery__success-lead',
      'If an account matches that email, an administrator has been notified to verify your identity and help you recover access.',
    );
    const note = el(
      'p',
      'recovery__success-note',
      'You can close this page — the team will reach out on a channel you’ve registered with us.',
    );

    box.append(badge, heading, lead, note);
    panel.appendChild(box);
  }
}
